import { Card } from "@app/design-system/components";
import { CustomFieldsNavButton } from "@app/features/shared/CustomFieldsNavButton";
import styles from "./EntityInfoRow.module.css";

export type EntityInfoRowProps = {
  /** Merchant-configured icon (`Service.icon` / `Vehicle.icon`) — left
   * blank (not a fallback glyph) when the entity has none, same "nothing to
   * show" rule as this row's trailing badge. */
  iconUrl?: string;
  label: string;
  value: string;
  /** Drives the trailing badge + arrow — Service's and Vehicle's own
   * options count (neither owns custom fields; see `Service.options` /
   * `Vehicle.options` in domain/people.types.ts). Generic name since this
   * row doesn't know or care which count it's showing. */
  badgeCount: number;
  onClick: () => void;
};

/**
 * Generic "icon + label/value + nav badge" row — Order Details' Service
 * and Vehicle sections are the same layout as `CustomerInfoRow` (icon,
 * two-line label/value, trailing badge+arrow), just for a different
 * entity, so this generalizes that shape rather than forking it twice.
 * Non-clickable except the trailing badge + arrow, same rule as every
 * other section on this page.
 */
export const EntityInfoRow = ({ iconUrl, label, value, badgeCount, onClick }: EntityInfoRowProps) => {
  const hasBadge = badgeCount > 0;

  return (
    <Card padded={false} grouped>
      <div className={styles.row}>
        {iconUrl ? <img src={iconUrl} alt="" className={styles.icon} /> : <span className={styles.icon} />}
        <div className={styles.body}>
          <div className={styles.label}>{label}</div>
          <div className={styles.value}>{value}</div>
        </div>
        {hasBadge ? <CustomFieldsNavButton count={badgeCount} onClick={onClick} /> : null}
      </div>
    </Card>
  );
};
